/**
 * CertificateDetailPage — Issue #362.
 *
 * Detail view + inline review / update / acknowledge / archive actions.
 *
 * 红线 (AI GUIDE §6):
 *   - 详情页仅显示 last4 + 状态；不渲染 cipher / hash。
 *   - 所有写入接口走 generated api-client，并以 expectedVersion 做乐观锁。
 *   - 没有“前端假状态”：状态、冻结、审核结果完全来自后端最新 DTO。
 */
import { useEffect, useState } from 'react';
import { Link, useRoute } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_CERTIFICATE_CRITICAL_ROLE_LABELS,
  HR_CERTIFICATE_REVIEW_STATUS_LABELS,
  HR_CERTIFICATE_STATUS_LABELS,
  maskCertificateNo,
  useHrCertificateDetail,
  useHrCertificateMutations,
  type HrCertificateDto,
} from '@/hooks/useHrCertificates';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('zh-CN');
}

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN');
}

export default function CertificateDetailPage() {
  const [, params] = useRoute<{ id: string }>('/hr/certificates/:id');
  const id = params?.id ?? '';
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.CERTIFICATE_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.CERTIFICATE_WRITE);
  const canReview = hasPermission(
    permissions,
    HR_PERMISSIONS.CERTIFICATE_REVIEW,
  );

  const {
    item,
    events,
    loading,
    error,
    fetchCertificate,
    fetchEvents,
  } = useHrCertificateDetail();
  const {
    loading: mutating,
    error: mutationError,
    updateCertificate,
    reviewCertificate,
    acknowledgeWarning,
    archiveCertificate,
  } = useHrCertificateMutations();

  const [reviewReason, setReviewReason] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');

  useEffect(() => {
    if (!canRead || !id) return;
    void fetchCertificate(id);
    void fetchEvents(id);
  }, [canRead, id, fetchCertificate, fetchEvents]);

  if (!canRead) return <AccessDenied resource="资质证照详情" />;
  if (!id) {
    return (
      <div className="p-6 text-sm text-slate-500">未指定证照 ID。</div>
    );
  }

  async function refresh() {
    await fetchCertificate(id);
    await fetchEvents(id);
  }

  async function onUpdateExpiresAt() {
    if (!item || !editExpiresAt) return;
    const result = await updateCertificate(item.id, {
      expectedVersion: item.version,
      expiresAt: new Date(editExpiresAt).toISOString(),
    });
    if (result) {
      setEditExpiresAt('');
      await refresh();
    }
  }

  async function onReview(
    decision: 'approve' | 'reject' | 'retract',
  ) {
    if (!item) return;
    const result = await reviewCertificate(item.id, {
      expectedVersion: item.version,
      decision,
      reason: reviewReason || undefined,
    });
    if (result) {
      setReviewReason('');
      await refresh();
    }
  }

  async function onAcknowledge(bucket: 'remind_30' | 'remind_7') {
    if (!item) return;
    const result = await acknowledgeWarning(item.id, {
      expectedVersion: item.version,
      bucket,
    });
    if (result) await refresh();
  }

  async function onArchive() {
    if (!item) return;
    const result = await archiveCertificate(item.id, {
      expectedVersion: item.version,
    });
    if (result) await refresh();
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            证照详情 <span className="ml-2 font-mono text-sm text-slate-500">{id}</span>
          </h1>
          <p className="text-sm text-slate-500">
            状态 / 审核 / 冻结状态实时反映后端最新值。
          </p>
        </div>
        <Link
          href="/hr/certificates"
          className="rounded border border-slate-300 px-3 py-1 text-sm"
        >
          ← 返回列表
        </Link>
      </header>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}
      {mutationError ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {mutationError.message}
        </Card>
      ) : null}

      {loading && !item ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : !item ? (
        <Card className="p-6 text-sm text-slate-500">
          未找到该证照记录，或没有权限查看。
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <DetailGrid item={item} />
          </Card>

          {canWrite ? (
            <Card className="p-4">
              <h2 className="mb-2 text-sm font-medium">续期 / 修正到期日</h2>
              <div className="flex items-end gap-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-600">新到期日</span>
                  <input
                    type="date"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={editExpiresAt}
                    onChange={(e) => setEditExpiresAt(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                  disabled={mutating || !editExpiresAt}
                  onClick={() => void onUpdateExpiresAt()}
                >
                  提交更新
                </button>
                <p className="text-xs text-slate-500">
                  更新会重置审核为“待审核”；
                  续期审批通过后冻结自动解除。
                </p>
              </div>
            </Card>
          ) : null}

          {canReview ? (
            <Card className="p-4">
              <h2 className="mb-2 text-sm font-medium">审核决策</h2>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-1 flex-col gap-1 text-sm">
                  <span className="text-slate-600">备注 / 原因</span>
                  <input
                    type="text"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    placeholder="可选"
                  />
                </label>
                <button
                  type="button"
                  className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                  disabled={mutating}
                  onClick={() => void onReview('approve')}
                >
                  批准
                </button>
                <button
                  type="button"
                  className="rounded bg-red-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                  disabled={mutating}
                  onClick={() => void onReview('reject')}
                >
                  驳回
                </button>
                <button
                  type="button"
                  className="rounded bg-slate-500 px-3 py-1 text-sm text-white disabled:opacity-50"
                  disabled={mutating || item.reviewStatus !== 'approved'}
                  onClick={() => void onReview('retract')}
                >
                  撤回审批
                </button>
              </div>
            </Card>
          ) : null}

          {canWrite ? (
            <Card className="p-4">
              <h2 className="mb-2 text-sm font-medium">预警送达确认</h2>
              <div className="flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  className="rounded border border-amber-300 px-3 py-1 disabled:opacity-50"
                  disabled={mutating || !!item.remind30SentAt}
                  onClick={() => void onAcknowledge('remind_30')}
                >
                  确认 30 天预警已送达
                </button>
                <button
                  type="button"
                  className="rounded border border-amber-500 px-3 py-1 disabled:opacity-50"
                  disabled={mutating || !!item.remind7SentAt}
                  onClick={() => void onAcknowledge('remind_7')}
                >
                  确认 7 天预警已送达
                </button>
                <button
                  type="button"
                  className="rounded border border-red-300 px-3 py-1 text-red-600 disabled:opacity-50"
                  disabled={mutating || item.status === 'archived'}
                  onClick={() => void onArchive()}
                >
                  归档证照
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                预警送达确认会落 remind_30 / remind_7 时间戳，避免重复通知。
              </p>
            </Card>
          ) : null}

          <Card className="overflow-hidden">
            <h2 className="border-b bg-slate-50 px-3 py-2 text-sm font-medium">
              审计事件
            </h2>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2">时间</th>
                  <th className="px-3 py-2">action</th>
                  <th className="px-3 py-2">from → to</th>
                  <th className="px-3 py-2">actor</th>
                  <th className="px-3 py-2">备注</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt) => (
                  <tr key={evt.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">
                      {fmtDateTime(evt.createdAt)}
                    </td>
                    <td className="px-3 py-2">{evt.action}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {(evt.fromStatus ?? '—') + ' → ' + (evt.toStatus ?? '—')}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {evt.actorType}:{evt.actorId}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {evt.reason ?? '—'}
                    </td>
                  </tr>
                ))}
                {events.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-6 text-center text-slate-400"
                    >
                      暂无审计事件
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function DetailGrid({ item }: { item: HrCertificateDto }) {
  return (
    <dl className="grid grid-cols-3 gap-y-3 text-sm">
      <DetailRow label="员工 ID" value={item.employeeId} mono />
      <DetailRow label="账号 userId" value={item.userId ?? '—'} mono />
      <DetailRow label="证书类型" value={item.certificateType} />
      <DetailRow
        label="证书号后四位"
        value={maskCertificateNo(item.certificateNoLast4)}
        mono
      />
      <DetailRow label="发证机关" value={item.issuingAuthority ?? '—'} />
      <DetailRow label="发证日期" value={fmtDate(item.issuedAt)} />
      <DetailRow label="到期日" value={fmtDate(item.expiresAt)} />
      <DetailRow
        label="证照状态"
        value={HR_CERTIFICATE_STATUS_LABELS[item.status]}
      />
      <DetailRow
        label="审核状态"
        value={HR_CERTIFICATE_REVIEW_STATUS_LABELS[item.reviewStatus]}
      />
      <DetailRow label="必备" value={item.mandatory ? '是' : '否'} />
      <DetailRow
        label="关键岗位"
        value={
          item.criticalRoleKey
            ? HR_CERTIFICATE_CRITICAL_ROLE_LABELS[item.criticalRoleKey] ??
              item.criticalRoleKey
            : '—'
        }
      />
      <DetailRow
        label="登录冻结"
        value={
          item.loginFreezeActive
            ? `已冻结 (${fmtDateTime(item.loginFreezeSetAt)})`
            : '未冻结'
        }
        emphasize={item.loginFreezeActive}
      />
      <DetailRow label="30 天预警" value={fmtDateTime(item.remind30SentAt)} />
      <DetailRow label="7 天预警" value={fmtDateTime(item.remind7SentAt)} />
      <DetailRow
        label="附件 OSS Key"
        value={item.attachmentObjectKey ?? '—'}
        mono
      />
      <DetailRow
        label="最近审核"
        value={
          item.lastReviewActorId
            ? `${item.lastReviewActorId} · ${fmtDateTime(
                item.lastReviewDecidedAt,
              )}`
            : '—'
        }
      />
      <DetailRow label="version" value={String(item.version)} mono />
      <DetailRow label="updatedAt" value={fmtDateTime(item.updatedAt)} />
      <div className="col-span-3 mt-2">
        <div className="text-xs text-slate-500">备注</div>
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          {item.notes && item.notes.trim() ? item.notes : '—'}
        </div>
      </div>
    </dl>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
  emphasize = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  emphasize?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd
        className={[
          'text-sm',
          mono ? 'font-mono text-xs' : '',
          emphasize ? 'text-red-700' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}
